import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { useGetFeedStatTypesQuery } from '@/api/feed';

interface StatTypeSelectionSectionProps {
  sportId?: string;
  questionType: string;
  selectedStatTypeCode?: string;
  onSelect: (statTypeCode: string) => void;
  disabled?: boolean;
  /** When true (over/under + fixture selected), stat type is mandatory */
  required?: boolean;
  error?: string;
}

// Map question type to category filter. Leader question types (DKPLAT-354)
// reuse the same player/team category split as their single-fixture
// counterparts so the stat-type dropdown shows the same filtered options.
function getCategoryForQuestionType(questionType: string): 'player' | 'team' {
  if (
    questionType === 'player' ||
    questionType === 'player_over_under' ||
    questionType === 'over_under' ||
    questionType === 'player_leader'
  ) {
    return 'player';
  }
  return 'team';
}

export function StatTypeSelectionSection({
  sportId,
  questionType,
  selectedStatTypeCode,
  onSelect,
  disabled = false,
  required = false,
  error,
}: StatTypeSelectionSectionProps) {
  const { data: statTypes, isLoading, error: queryError } = useGetFeedStatTypesQuery(sportId);

  // Filter stat types based on question type
  const filteredStatTypes = useMemo(() => {
    if (!statTypes) return [];
    const category = getCategoryForQuestionType(questionType);
    return statTypes.filter(statType => statType.category === category);
  }, [statTypes, questionType]);

  if (!sportId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading stat types...</span>
      </div>
    );
  }

  if (queryError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load stat types. Please try again.</AlertDescription>
      </Alert>
    );
  }

  if (!filteredStatTypes || filteredStatTypes.length === 0) {
    return (
      <Alert>
        <AlertDescription>
          No stat types available for this sport and question type.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="stat-type-select">Stat Type {required ? '(Required)' : '(Optional)'}</Label>
      <Select
        value={required ? selectedStatTypeCode || '' : selectedStatTypeCode || '__none__'}
        onValueChange={value => {
          if (!required && value === '__none__') {
            onSelect('');
          } else {
            onSelect(value);
          }
        }}
        disabled={disabled}
      >
        <SelectTrigger id="stat-type-select" className={error ? 'border-red-500' : undefined}>
          <SelectValue
            placeholder={
              required ? 'Select a stat type (required)' : 'Select a stat type (optional)'
            }
          />
        </SelectTrigger>
        <SelectContent>
          {!required && <SelectItem value="__none__">None</SelectItem>}
          {filteredStatTypes.map(statType => (
            <SelectItem key={statType.code} value={statType.code}>
              {statType.name}
              {statType.unit && ` (${statType.unit})`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : (
        <p className="text-sm text-muted-foreground">
          Choose the statistic that this question will be based on{' '}
          {required ? '(required for over/under with fixture)' : '(optional)'}
        </p>
      )}
    </div>
  );
}
